import React                   from 'react';
import SearchResultsFilter     from "./SearchResultsFilter";
import PropTypes               from 'prop-types';
import Tile                    from "../TileGrid/Tile";
import AlbumItem               from "../Video/Item/AlbumItem";
import BlogPostCard            from "../Magazine/BlogPostCard";
import {formatDuration}        from "./SearchResultsHelper";
import Pagination              from "../Pagination";
import Translations            from "../../utils/Translations";
import SearchResultsVideosGrid from "./SearchResultsVideosGrid";
import LogoLoader              from "../Loader/LogoLoader";
import SearchResultsModelsGrid from "./SearchResultsModelsGrid";
import { generateKey } from '../../utils/CommonUtils';

class SearchResultsPage extends React.PureComponent {

	constructor(props) {
		super(props);

		this.onPaginationChange = this.onPaginationChange.bind(this);
		this.onResetFilter      = this.onResetFilter.bind(this);

		this.getGrid       = this.getGrid.bind(this);
		this.getPagination = this.getPagination.bind(this);
	}

	onPaginationChange(newPage) {
		this.props.onPaginationChange(newPage);
	}

	onResetFilter() {
		this.props.onResetFilter();
	}

	getNoResultsPage(idx) {
		return (
			<div key={generateKey('no-pagination', idx)} className="searchresults_noresults">
				<div className="searchresults_noresults-header">
					{Translations.get('MessengerSearchNoResults')}
				</div>
				<div onClick={this.onResetFilter} className="searchresults_noresults-button">
					{Translations.get('VideoGridItemsEmptySubheadline')}
				</div>
			</div>
		);
	}

	getGrid(idx) {
		if (!this.props.result || this.props.forceVxLoader) {
			return <div key={generateKey('grid', idx)} className="h-bg-color-mood-4 h-pt-30 h-pb-30 h-pos-relative"><LogoLoader className="-static" pulse={true}/></div>;
		} else if (this.props.result.total === 0) {
			return this.getNoResultsPage(idx);
		}

		const items = [];

		switch (this.props.result.type) {
			case 'models':
				const modelsTile = <SearchResultsModelsGrid key={generateKey('models', items.length)} models={this.props.result.items}/>;
				items.push(modelsTile);
				break;
			case 'videos':
				const tile = <SearchResultsVideosGrid key={generateKey('videos', items.length)} videos={this.props.result.items}/>;
				items.push(tile);
				break;
			case 'galleries':
				for (let i = 0; i < this.props.result.items.length; i++) {
					const item = this.props.result.items[i].gallery;

					const previewPictures = [];

					if (item.previews) {
						for (let i = 0; i < item.previews.length; i++) {
							previewPictures.push([{
								src:    item.previews[i].url,
								format: 'w320',
								height: item.previews[i].height,
								width:  320,
								id:     item.previews[i].id,
							}]);
						}
					}

					const tile = <Tile key={generateKey('galleries', items.length)} gridConfig={{usePjax: true, showButton: true, totalCount: item.photosCount}}
					                   guestIsLoggedIn={true}
					                   tileData={{
						                   type:               Tile.type.PHOTO_ALBUM_GRID_TILE,
						                   previewPictureUrls: previewPictures,
						                   targetUrl:          item.linkVX,
						                   title:              item.title,
						                   description:        item.description,
						                   tileIdentifier:     item.id,
						                   plateType:          'DEFAULT_PLATE',
						                   elementCount:       item.photosCount,
						                   isActorNameVisible: !!(item.model && item.model.name),
						                   actorName:          item.model ? item.model.name : '',
					                   }}
					                   tileIndex={i}
					                   gridTileClassName={"searchresults_grid-item -photos"}
					                   useTileWithSecondary={true}
					                   gridMode={'TILEGRID_MODE_GRID'}
					/>;

					items.push(tile);
				}
				break;
			case 'mediathek':
				for (let i = 0; i < this.props.result.items.length; i++) {
					const item = this.props.result.items[i].mediathekVideo;

					const tile = <AlbumItem key={generateKey(`mediathek-${item.title}`, items.length)}
											title={item.title}
					                        previewPicture={item.previewPicture}
					                        duration={formatDuration(item.duration)}
					                        url={item.linkVX}
					                        showFsk={true}
					                        avs={item.fsk}
					/>;

					items.push(tile);
				}
				break;
			case 'blog':
				for (let i = 0; i < this.props.result.items.length; i++) {
					const item = this.props.result.items[i].blogPost;

					const tile = <BlogPostCard key={generateKey(`blog-${items.title}`, items.length)}
											   title={item.title}
					                           subTitle={item.subTitle}
					                           id={item.id}
					                           links={{de: item.linkVX, en: item.linkVX}}
					                           image={item.previewPicture}
					                           smallImage={item.previewPicture}
					                           date={item.date}
					                           excerpt={item.excerpt}
					/>;

					items.push(tile);
				}
				break;
			default:
				return null;
		}

		return <div key={generateKey('grid', idx)} className={"searchresults_grid -" + this.props.result.type}>{items}</div>;
	}

	getPagination(idx) {
		if (this.props.pagination.maxPage < 2) {
			return null;
		}

		return (
			<div key={generateKey('pagination', idx)} className={"searchresults_pagination"}>
				<Pagination currentPage={this.props.pagination.currentPage}
				            maxPage={this.props.pagination.maxPage}
				            goToFn={this.onPaginationChange}
				/>
			</div>
		);
	}

	render() {
		const content = [];

		content.push(
			<SearchResultsFilter key={generateKey('filter', content.length)}
								 filterConfig={this.props.filterConfig}
			                     filter={this.props.filter}
			                     onFilterChange={this.props.onFilterChange}
			/>
		);
		content.push(this.getGrid(content.length));
		content.push(this.getPagination(content.length));

		return <div className={"searchresults_body"}>{content}</div>;
	}

}

SearchResultsPage.propTypes = {
	filterConfig:       PropTypes.shape({
		showGender: PropTypes.bool,
		showOnline: PropTypes.bool,
		sorting:    PropTypes.arrayOf(PropTypes.string),
	}),
	filter:             PropTypes.shape({
		gender:  PropTypes.arrayOf(PropTypes.string),
		sorting: PropTypes.string,
	}),
	onFilterChange:     PropTypes.func.isRequired,
	onPaginationChange: PropTypes.func.isRequired,
	onResetFilter:      PropTypes.func.isRequired,
	result:             PropTypes.shape({
		type:  PropTypes.string,
		total: PropTypes.number,
		items: PropTypes.array,
	}),
	pagination:         PropTypes.shape({
		total:       PropTypes.number,
		currentPage: PropTypes.number,
		maxPage:     PropTypes.number,
	}),
	forceVxLoader:      PropTypes.bool,
};

SearchResultsPage.defaultProps = {
	result:        null,
	forceVxLoader: false,
};

export default SearchResultsPage;